import React, { useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { Stepper } from "./Stepper";
import { t } from "i18next";
import { useLocation } from "react-router";
import { ProductsService } from "../../service";
import { IProducts } from "../../dto/IProducts";
import { ErrorToast, SuccessToast } from "../../../../shared/toasters/toasters";
import { formatDate } from "../../../../shared/functions/Functions";
import { CustomImageHandler } from "../../../../shared/components/CustomImageHandler";
import { useSelector } from "react-redux";
import { IAllStates } from "../../../../store/rootReducer";

export const StepMedia = () => {
  const location: any = useLocation();
  const checkedValues = location?.state?.checkedValues || [];
  const [products, setProducts] = useState<IProducts[]>();
  const [product, setProduct] = useState<IProducts>();
  const [selectedProduct, setSelectedProduct] = useState<any>();
  const { translationLanguageEnum } = useSelector(
    (state: IAllStates) => state.enums
  );

  const [requirements, setRequirements] = useState({
    removeOldFiles: false,
  });

  function toggleCheckbox(field: string) {
    setRequirements((prev: any) => {
      if (field === "removeOldFiles") {
        return { ...prev, removeOldFiles: !prev.removeOldFiles };
      }
      const groupPrefixes = ["all_photos", "all_documents", "all_videos"];
      if (groupPrefixes.includes(field)) {
        const isChecked = !prev[field];
        const newState = { ...prev, [field]: isChecked };
        const dataKeyMap = {
          all_photos: "pictures",
          all_documents: "documents",
          all_videos: "videos",
        };
        const dataKey = (dataKeyMap as any)[field];
        const items = selectedProduct?.[dataKey] || [];
        items.forEach((_: any, idx: number) => {
          newState[`${field}_${idx}`] = isChecked;
        });

        return newState;
      }
      const newValue = !prev[field];
      const newState = { ...prev, [field]: newValue };
      const groupPrefix = field.split("_").slice(0, 2).join("_");
      if (groupPrefixes.includes(groupPrefix)) {
        const dataKeyMap = {
          all_photos: "pictures",
          all_documents: "documents",
          all_videos: "videos",
        };
        const dataKey = (dataKeyMap as any)[groupPrefix];
        const items = selectedProduct?.[dataKey] || [];
        const allChecked = items.every(
          (_: any, idx: number) => newState[`${groupPrefix}_${idx}`]
        );

        newState[groupPrefix] = allChecked;
      }

      return newState;
    });
  }
  function preparePayload(requirements: any, selectedProduct: any) {
    const payload: any = {
      removeOldFiles: !!requirements.removeOldFiles,
    };
    const mapping = {
      all_photos: { dataKey: "pictures", backendKey: "photos" },
      all_documents: { dataKey: "documents", backendKey: "documents" },
      all_videos: { dataKey: "videos", backendKey: "videos" },
    };

    Object.entries(mapping).forEach(
      ([fieldPrefix, { dataKey, backendKey }]) => {
        const items = selectedProduct[dataKey] || [];
        const selectedIds = items
          .map((item: any, idx: number) => {
            const checkboxKey = `${fieldPrefix}_${idx}`;
            if (requirements[checkboxKey]) {
              return item.id;
            }
            return null;
          })
          .filter(Boolean);

        payload[backendKey] = selectedIds;
      }
    );

    return payload;
  }
  const selectedProducts = JSON.parse(
    localStorage.getItem("selectedProducts") as string
  );
  const selectChangeHandler = (selectedProduct: IProducts) => {
    setProduct(selectedProduct);
    selectedProduct?.id &&
      ProductsService.getManageMedia(+selectedProduct?.id)
        .then((response) => {
          setSelectedProduct(response);
        })
        .catch((error) => {
          ErrorToast(error);
        });
  };
  const handleSubmit = (e: any) => {
    e.preventDefault();
    const dataToSend = preparePayload(requirements, selectedProduct);
    const filteredArray = selectedProducts?.filter(
      (item: number) => item != product?.id
    );
    ProductsService.copyManageMedia({
      fromProduct: product?.id,
      toProducts: filteredArray,
      ...dataToSend,
    })
      .then((response) => {
        SuccessToast(`${t("toasts.successfully_added")}`);
      })
      .catch((error) => ErrorToast(error));
  };
  useEffect(() => {
    if (selectedProducts?.length > 1) {
      const checkedValuesAsStrings = selectedProducts.map(String);
      ProductsService.getManageProductsByIds({ ids: checkedValuesAsStrings })
        .then((response) => {
          setProducts(response);
        })
        .catch((error) => ErrorToast(error));
    } else {
      return;
    }
  }, []);

  return (
    <>
      <div className="card">
        <Helmet>
          <title>{t("manage.products_manage_media")}</title>
        </Helmet>
        <div className="">
          <Stepper step={"MEDIA"}></Stepper>
          <>
            {product?.id ? (
              <>
                <div className="mt-3">
                  <div className="card-header d-flex align-items-center justify-content-between my-0">
                    <h4 className="card-title mb-0">
                      {t("manage.selected_product")}
                    </h4>
                    <button
                      className="btn btn-secondary ms-2"
                      onClick={(e) => handleSubmit(e)}
                    >
                      {t("global_v2.copy")}
                    </button>
                  </div>

                  <div className="card-body">
                    <Table className="align-middle table-nowrap ">
                      <thead className="table-light">
                        <tr>
                          <th scope="col">{`${t("global.id")}`}</th>
                          <th scope="col">{`${t("global.aenCode")}`}</th>
                          <th scope="col">{`${t("products.product")}`}</th>
                          <th scope="col">{`${t("products.category")}`}</th>
                          <th scope="col">{`${t("products.createdAt")}`}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr key={product?.id}>
                          <td>
                            <span>#{product?.id}</span>
                          </td>
                          <td>
                            <span className="text-dark">
                              {product?.eanCode ?? "-"}
                            </span>
                          </td>
                          <td>
                            <span className="d-flex justify-content-start">
                              <div className="d-flex align-items-center">
                                <div className="flex-shrink-0 me-3">
                                  <div className="avatar-sm bg-light rounded p-1">
                                    <CustomImageHandler
                                      path={product?.productPictures?.[0]?.path}
                                      alt="Product"
                                      classes="avatar-xs h-100 w-100"
                                    />
                                  </div>
                                </div>
                                <div className="flex-grow-1">
                                  <h5 className="fs-14 mb-1">
                                    <>{product?.title ?? "-"}</>
                                  </h5>
                                </div>
                              </div>
                            </span>
                          </td>

                          <td>
                            {(product?.category as any)?.title ??
                              "Without category"}
                          </td>
                          <td>{formatDate((product as any)?.createdAt)}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </div>
                <div className="card-body pt-0">
                  <div className="row">
                    {[
                      { field: "removeOldFiles" },
                      { field: "all_photos", dataKey: "pictures" },
                      { field: "all_documents", dataKey: "documents" },
                      { field: "all_videos", dataKey: "videos" },
                    ].map(({ field, dataKey }) => {
                      const itemsCount = dataKey
                        ? selectedProduct?.[dataKey]?.length || 0
                        : 0;
                      const disabled = dataKey ? itemsCount < 2 : false;
                      return (
                        <div
                          className="col-md-3"
                          key={field}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start",
                          }}
                        >
                          <div className="form-check my-1">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={field}
                              checked={(requirements as any)[field]}
                              onChange={() => toggleCheckbox(field)}
                              disabled={disabled}
                            />
                            <label className="form-check-label" htmlFor={field}>
                              {field === "removeOldFiles" ? (
                                <span className="text-danger">
                                  {t(`mapping.${field}`)}
                                </span>
                              ) : (
                                <span className="text-success">
                                  {t(`mapping.${field}`)}
                                </span>
                              )}
                            </label>
                          </div>

                          {/* Ostali sadržaj */}
                          {dataKey &&
                            (selectedProduct?.[dataKey] || []).map(
                              (item: any, idx: number) => {
                                const name = item?.file?.nameOriginal ?? "-";
                                const url = item?.path ?? "";
                                const fieldName = `${field}_${idx}`;

                                return (
                                  <div
                                    key={fieldName}
                                    className="form-check my-1 d-flex"
                                    style={{ gap: 2, alignItems: "center" }}
                                  >
                                    <div
                                      className="d-flex align-items-center"
                                      style={{
                                        width: dataKey === "pictures" ? 50 : 0,
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        className="form-check-input mb-1"
                                        id={fieldName}
                                        style={{ width: 14.3, height: 14.3 }}
                                        checked={
                                          (requirements as any)[fieldName] ||
                                          false
                                        }
                                        onChange={() =>
                                          toggleCheckbox(fieldName)
                                        }
                                      />

                                      {dataKey === "pictures" && url && (
                                        <img
                                          src={url}
                                          alt={name}
                                          style={{
                                            width: 40,
                                            height: 40,
                                            objectFit: "cover",
                                            borderRadius: 4,
                                            marginLeft: "10px",
                                          }}
                                        />
                                      )}
                                    </div>

                                    <label
                                      className="form-check-label text-muted small"
                                      htmlFor={fieldName}
                                      style={{ flex: 1 }}
                                    >
                                      {name}
                                    </label>
                                  </div>
                                );
                              }
                            )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <p>{t("manage.no_source_product")}</p>
            )}
          </>
          <>
            <div className="card-header my-0">
              <h4 className="card-title mb-0">
                {t("manage.list_of_products")}
              </h4>
            </div>
            <div className="card-body">
              <Table className="align-middle table-nowrap mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{t("global.select")}</th>
                    <th scope="col">{`${t("global.id")}`}</th>
                    <th scope="col">{`${t("global.aenCode")}`}</th>
                    <th scope="col">{`${t("products.product")}`}</th>
                    <th scope="col">{`${t("products.category")}`}</th>
                    <th scope="col">{`${t("products.createdAt")}`}</th>
                  </tr>
                </thead>
                <tbody>
                  {products?.map((product: any) => (
                    <tr key={product?.id}>
                      <td>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="format"
                          id={`flexRadioDefault${product?.id}`}
                          onChange={() => selectChangeHandler(product)}
                        />
                      </td>
                      <td>
                        <span>#{product?.id}</span>
                      </td>
                      <td>
                        <span className="text-dark">
                          {product?.eanCode ?? "-"}
                        </span>
                      </td>
                      <td>
                        <span className="d-flex justify-content-start">
                          <div className="d-flex align-items-center">
                            <div className="flex-shrink-0 me-3">
                              <div className="avatar-sm bg-light rounded p-1">
                                <CustomImageHandler
                                  path={product?.productPictures?.[0]?.path}
                                  alt="Product"
                                  classes="avatar-xs h-100 w-100"
                                />
                              </div>
                            </div>
                            <div className="flex-grow-1">
                              <h5 className="fs-14 mb-1">
                                <>{product?.title ?? "-"}</>
                              </h5>
                            </div>
                          </div>
                        </span>
                      </td>

                      <td>
                        {(product?.category as any)?.title ??
                          "Without category"}
                      </td>
                      <td>{formatDate(product?.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        </div>
      </div>
    </>
  );
};
